import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import axios from "axios";
import { CheckCircle, Rocket, TrendingUp, ShoppingBag, ArrowRight } from "lucide-react";
import { SITE } from "../data/seoPages";

const API = process.env.REACT_APP_BACKEND_URL;

export default function BrandCaseStudyPage() {
  const { slug } = useParams();
  const [cs, setCs] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    setCs(null); setErr(false);
    axios.get(`${API}/api/public/customer-logos/case-studies/${slug}`).then((r) => setCs(r.data)).catch(() => setErr(true));
  }, [slug]);

  if (err) return (
    <main className="min-h-screen bg-gray-950 text-white flex items-center justify-center pt-24">
      <div className="text-center"><p className="text-2xl font-black mb-4">Case study not found</p><Link to="/" className="text-orange-400 underline">← Back to BrandBorn</Link></div>
    </main>
  );
  if (!cs) return <main className="min-h-screen bg-gray-950 pt-24" />;

  const brand = cs.brand_name;
  const platforms = (cs.platforms || "").split(",").map((p) => p.trim()).filter(Boolean);
  const keywords = (cs.keywords || "").split(",").map((k) => k.trim()).filter(Boolean);
  const pageUrl = `${SITE}/case-study/${cs.slug}`;
  const title = `${brand} Case Study — Launched on ${platforms.join(", ") || "Top Marketplaces"} by BrandBorn`;
  const desc = `How BrandBorn onboarded and launched ${brand}${cs.category ? ` (${cs.category})` : ""} on ${platforms.join(", ") || "leading Indian marketplaces"}. ${cs.results || "Full marketplace onboarding case study by India's #1 brand onboarding agency."}`.slice(0, 158);
  const paras = (t) => (t || "").split(/\n+/).map((s) => s.trim()).filter(Boolean);

  return (
    <main className="bg-gray-950 text-white min-h-screen pt-28 pb-20 px-4" data-testid="brand-case-study">
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={desc} />
        {keywords.length > 0 && <meta name="keywords" content={[...keywords, `${brand} case study`, `${brand} brandborn`, ...platforms.map((p) => `${brand} on ${p}`)].join(", ")} />}
        {keywords.length === 0 && <meta name="keywords" content={[`${brand}`, `${brand} case study`, `${brand} brand`, `${brand} online`, ...platforms.map((p) => `${brand} on ${p}`), "BrandBorn case study"].join(", ")} />}
        <link rel="canonical" href={pageUrl} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={desc} />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:image" content={`${API}/api/public/customer-logos/${cs.id}/image`} />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org", "@type": "Article",
          "headline": `${brand} — Marketplace Onboarding Case Study by BrandBorn`,
          "description": desc, "image": `${API}/api/public/customer-logos/${cs.id}/image`,
          "author": { "@type": "Organization", "name": "BrandBorn", "url": SITE },
          "publisher": { "@type": "Organization", "name": "BrandBorn", "url": SITE },
          "mainEntityOfPage": pageUrl,
          "about": { "@type": "Brand", "name": brand }
        })}</script>
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org", "@type": "BreadcrumbList",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "BrandBorn", "item": SITE },
            { "@type": "ListItem", "position": 2, "name": `${brand} Case Study`, "item": pageUrl }
          ]
        })}</script>
      </Helmet>

      <div className="max-w-4xl mx-auto">
        <p className="text-orange-400 text-xs font-black uppercase tracking-[0.2em] mb-4 text-center">BrandBorn Success Story</p>
        <div className="bg-white rounded-3xl p-8 max-w-sm mx-auto mb-8 shadow-2xl flex items-center justify-center h-40">
          <img src={`${API}/api/public/customer-logos/${cs.id}/image`} alt={`${brand} logo — launched by BrandBorn`} className="max-h-28 max-w-full object-contain" />
        </div>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-center mb-4">
          How BrandBorn Launched <span className="bg-gradient-to-r from-red-500 via-orange-400 to-yellow-400 bg-clip-text text-transparent">{brand}</span>
        </h1>
        <p className="text-gray-400 text-center max-w-2xl mx-auto mb-10">
          {brand}{cs.category ? `, a ${cs.category} brand,` : ""} was onboarded and launched by BrandBorn — India's #1 marketplace onboarding agency{platforms.length ? ` — on ${platforms.join(", ")}` : ""}.
        </p>

        {platforms.length > 0 && (
          <div className="flex flex-wrap justify-center gap-2 mb-10">
            {platforms.map((p) => (
              <span key={p} className="inline-flex items-center gap-1.5 px-4 py-2 bg-white/[0.07] border border-white/15 rounded-full text-sm font-bold"><ShoppingBag size={13} className="text-orange-400" /> {p}</span>
            ))}
          </div>
        )}

        <div className="flex flex-wrap justify-center gap-6 md:gap-12 mb-14 text-center">
          {[
            [cs.timeline || "14 Days", "Launch Timeline"],
            [platforms.length ? `${platforms.length} Marketplace${platforms.length > 1 ? "s" : ""}` : "Multi-Platform", "Launched On"],
            [cs.category || "D2C Brand", "Category"],
          ].map(([v, l]) => (
            <div key={l}>
              <div className="text-2xl md:text-3xl font-black bg-gradient-to-r from-red-500 to-orange-400 bg-clip-text text-transparent">{v}</div>
              <div className="text-[11px] uppercase tracking-widest text-gray-500 font-bold mt-1">{l}</div>
            </div>
          ))}
        </div>

        <article className="max-w-3xl mx-auto text-gray-300 text-base md:text-lg leading-relaxed space-y-5 mb-14">
          <h2 className="text-2xl md:text-3xl font-black text-white flex items-center gap-3"><Rocket size={24} className="text-orange-400" /> How BrandBorn Launched {brand}</h2>
          {(paras(cs.story).length ? paras(cs.story) : [
            `BrandBorn handled ${brand}'s complete marketplace onboarding — seller registration, documentation, brand approvals, AI-powered cataloging via EcomCatalog.ai and go-live${platforms.length ? ` across ${platforms.join(", ")}` : ""}.`,
          ]).map((p, i) => <p key={i}>{p}</p>)}

          {cs.timeline && (
            <p className="flex items-start gap-2"><CheckCircle size={20} className="text-emerald-400 mt-1 flex-shrink-0" /><span><strong className="text-white">Timeline:</strong> {cs.timeline}</span></p>
          )}

          <h2 className="text-2xl md:text-3xl font-black text-white flex items-center gap-3 pt-4"><TrendingUp size={24} className="text-orange-400" /> The Results: {brand} Today</h2>
          {(paras(cs.results).length ? paras(cs.results) : [
            `${brand} is now live and selling${platforms.length ? ` on ${platforms.join(", ")}` : " on leading Indian marketplaces"} — one of 1000+ successful launches by BrandBorn.`,
          ]).map((p, i) => <p key={i}>{p}</p>)}

          <h2 className="text-2xl md:text-3xl font-black text-white pt-4">About the {brand} × BrandBorn Partnership</h2>
          <p>
            {brand} partnered with <Link to="/" className="text-orange-400 underline">BrandBorn</Link>, the Official Myntra OMS Partner featured in The Times of India and <Link to="/economic-times" className="text-orange-400 underline">The Economic Times (ET AI Innovator)</Link>, for its marketplace onboarding. From seller account creation and brand registry to AI-generated listings with EcomCatalog.ai, BrandBorn managed the end-to-end launch{platforms.length ? ` of ${brand} across ${platforms.join(", ")}` : ""}.
          </p>
        </article>

        <div className="bg-gradient-to-r from-red-600 to-orange-500 rounded-3xl p-8 md:p-10 text-center">
          <h2 className="text-2xl md:text-3xl font-black mb-3">Want a launch like {brand}?</h2>
          <p className="text-white/80 text-sm mb-6">Join 1000+ brands launched by BrandBorn on 70+ marketplaces.</p>
          <Link to="/" data-testid="case-study-cta" className="inline-flex items-center gap-2 px-8 py-4 bg-white text-red-600 rounded-full font-black uppercase tracking-wider text-sm hover:scale-105 transition-transform">
            Get Started <ArrowRight size={16} />
          </Link>
        </div>
      </div>
    </main>
  );
}
